import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Card } from '@/components/ui/Card';

interface QualificationRule {
  id: string;
  stage: string;
  criteriaJson: string;
  updatedAt: string | null;
}

const STAGE_KEYS: Record<string, string> = {
  MQL: 'leadQualification.stage_mql',
  SQL: 'leadQualification.stage_sql',
  SAL: 'leadQualification.stage_sal',
};

/**
 * Story 81.1 — Admin UI for MQL/SQL/SAL qualification rule thresholds.
 *
 * Displays three editable JSON panels, one per stage. Each saves independently.
 * No chart library; uses simple flexbox layout consistent with TenantSettingsPage.
 */
export function LeadQualificationPage() {
  const { t } = useTranslation();
  const [rules, setRules] = useState<QualificationRule[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [messages, setMessages] = useState<Record<string, { ok: boolean; text: string }>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient
      .get<QualificationRule[]>('/lead-qualification/rules')
      .then((res) => {
        setRules(res.data);
        const init: Record<string, string> = {};
        for (const r of res.data) {
          try {
            init[r.stage] = JSON.stringify(JSON.parse(r.criteriaJson), null, 2);
          } catch {
            init[r.stage] = r.criteriaJson;
          }
        }
        setDrafts(init);
      })
      .catch(() => setError(t('leadQualification.error_load')))
      .finally(() => setLoading(false));
  }, [t]);

  async function save(stage: string) {
    setSaving((s) => ({ ...s, [stage]: true }));
    setMessages((m) => ({ ...m, [stage]: { ok: false, text: '' } }));
    try {
      let json = drafts[stage] ?? '{}';
      try {
        json = JSON.stringify(JSON.parse(json));
      } catch {
        setMessages((m) => ({
          ...m,
          [stage]: { ok: false, text: t('leadQualification.error_save') },
        }));
        return;
      }
      const res = await apiClient.put<QualificationRule>(
        `/lead-qualification/rules/${stage}`,
        { criteriaJson: json },
      );
      setRules((prev) =>
        prev.map((r) => (r.stage === stage ? res.data : r)),
      );
      setMessages((m) => ({ ...m, [stage]: { ok: true, text: t('leadQualification.saved') } }));
    } catch {
      setMessages((m) => ({
        ...m,
        [stage]: { ok: false, text: t('leadQualification.error_save') },
      }));
    } finally {
      setSaving((s) => ({ ...s, [stage]: false }));
    }
  }

  if (loading) {
    return <p className="p-6 text-sm text-muted-foreground">{t('leadQualification.loading')}</p>;
  }

  if (error) {
    return <p className="p-6 text-sm text-destructive">{error}</p>;
  }

  const stages = rules.length > 0 ? rules.map((r) => r.stage) : ['MQL', 'SQL', 'SAL'];

  return (
    <div className="space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold">{t('leadQualification.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('leadQualification.subtitle')}</p>
      </header>

      <div className="grid gap-4 md:grid-cols-3">
        {stages.map((stage) => {
          const msg = messages[stage];
          const isSaving = saving[stage] ?? false;
          return (
            <Card key={stage} className="space-y-3">
              <h2 className="text-sm font-semibold">
                {t(STAGE_KEYS[stage] ?? stage)}
              </h2>

              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">
                  {t('leadQualification.criteria_json')}
                </label>
                <textarea
                  rows={8}
                  className="w-full rounded-md border bg-background px-3 py-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-primary"
                  value={drafts[stage] ?? ''}
                  onChange={(e) =>
                    setDrafts((d) => ({ ...d, [stage]: e.target.value }))
                  }
                  placeholder={t('leadQualification.criteria_hint')}
                />
                <p className="text-[10px] text-muted-foreground">
                  {t('leadQualification.criteria_hint')}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => save(stage)}
                  disabled={isSaving}
                  className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
                >
                  {isSaving ? t('leadQualification.saving') : t('leadQualification.save')}
                </button>
                {msg?.text && (
                  <span
                    className={`text-xs font-medium ${msg.ok ? 'text-emerald-600' : 'text-destructive'}`}
                  >
                    {msg.text}
                  </span>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
